import { Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { DemoService } from './../../../../demo.service';
import { LegalDateTimeService } from './../../../../services/legal-datetime.service';
import {
  ActivityMetricsV2Breakdown,
  ActivityMetricsV2BackendDecision,
  ActivityMetricsV2ExportResponse,
  ActivityMetricsV2MetricResult,
  ActivityMetricsV2Response,
  ActivityMetricsV2Search,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2SubjectRollup,
} from './../../../../shared/models/activity-metrics-v2.model';
import {
  ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY,
  ACTIVITY_METRICS_V2_DEFAULT_SOURCES,
  ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
  getActivityMetricsV2PreviousFullMonthWindow,
} from './../activity-metrics-v2-indexed-reporting.util';

interface LegacyMetricCardConfig {
  card_key: string;
  card_label: string;
  card_title: string;
  metric_key: string;
  colour: string;
}

interface LegacyMetricChartRow {
  subject_number: string;
  metric_1: number | null;
  metric_2: number | null;
  metric_3: number | null;
  metric_4: number | null;
  metric_5: number | null;
  metric_6: number | null;
}

interface LegacyMetricSummaryChartRow {
  label: string;
  average_days: number | null;
  benchmark_days: number | null;
}

interface LegacySubjectGridRow {
  job_id: number | null;
  subject_id: number | null;
  latest_subject_service_id: number | null | undefined;
  subject_number: string;
  subject_name: string;
  attempt_count: number;
  last_activity_at: string | null;
  latest_resolution_outcome_name: string | null | undefined;
  latest_service_type_name: string | null | undefined;
  region_source: string | null | undefined;
  job_status: string | null | undefined;
  has_stage_progression: boolean;
  was_spawned_from_subject: boolean;
  metric_1: string;
  metric_2: string;
  metric_3: string;
  metric_4: string;
  metric_5: string;
  metric_6: string;
}

interface LegacyBreakdownTable {
  key: string;
  label: string;
  rows: Array<{
    label: string;
    count: number;
    event_count: number | null | undefined;
    attempt_count: number | null | undefined;
    rate: number | null | undefined;
  }>;
}

interface LegacyTimelineRow {
  job_id: number | null | undefined;
  subject_id: number | null | undefined;
  subject_service_id: number | null | undefined;
  event_id: string;
  when: string | null;
  subject_number: string;
  subject_name: string;
  source: string;
  label: string;
  action: string | null | undefined;
  agent_name: string | null | undefined;
  service_type_name: string | null | undefined;
  resolution_outcome_name: string | null | undefined;
}

@Component({
  selector: 'app-agent-performance-activity-metrics-attempt-attempts',
  templateUrl: './agent-performance-activity-metrics-attempt-attempts.component.html',
  styleUrls: ['./agent-performance-activity-metrics-attempt-attempts.component.scss']
})
export class AgentPerformanceActivityMetricsAttemptAttemptsComponent implements OnInit, OnDestroy {
  readonly brand_fields: Object = { text: 'NameAndAddress', value: 'brand_id' };
  readonly dateFormat = 'dd/MM/yyyy hh:mm a';
  readonly backendModeOptions: Array<{ label: string; value: ActivityMetricsV2Search['backend_mode']; help: string }> = [
    {
      label: 'Auto',
      value: 'auto',
      help: 'Use reporting tables when the selected window is indexed, otherwise allow the API decision path.',
    },
    {
      label: 'Reporting Tables',
      value: 'event_store',
      help: 'Use indexed Metrics 2.0 data. This is the canonical audit/reporting mode.',
    },
    {
      label: 'Live Tables',
      value: 'live',
      help: 'Use raw operational tables for scoped classic validation. Best for one client, job, or subject.',
    },
  ];

  readonly metricCardConfig: LegacyMetricCardConfig[] = [
    {
      card_key: 'metric_1',
      card_label: 'Metric 1',
      card_title: 'Average No. Of Days From Instructions Sent To 1st Attempt',
      metric_key: 'attempt.time_instructions_to_first_actual',
      colour: '#457EDD',
    },
    {
      card_key: 'metric_2',
      card_label: 'Metric 2',
      card_title: 'Average No. Of Days From Instructions Sent To 2nd Attempt',
      metric_key: 'attempt.time_instructions_to_second_actual',
      colour: '#1F1F1F',
    },
    {
      card_key: 'metric_3',
      card_label: 'Metric 3',
      card_title: 'Average No. Of Days Between Agents 1st - 2nd Attempt',
      metric_key: 'attempt.time_first_to_second_actual',
      colour: '#A2D369',
    },
    {
      card_key: 'metric_4',
      card_label: 'Metric 4',
      card_title: 'Average No. Of Days From Instructions Sent To 3rd Attempt',
      metric_key: 'attempt.time_instructions_to_third_actual',
      colour: '#FA6F50',
    },
    {
      card_key: 'metric_5',
      card_label: 'Metric 5',
      card_title: 'Average No. Of Days Between Agents 2nd - 3rd Attempt',
      metric_key: 'attempt.time_second_to_third_actual',
      colour: '#FECF56',
    },
    {
      card_key: 'metric_6',
      card_label: 'Metric 6',
      card_title: 'Average No. Of Days Between Job Opened And Closed / Final Touch',
      metric_key: 'job.time_open_to_closed_or_last_touch',
      colour: '#62ADE5',
    },
  ];

  readonly supportMetricCardConfig: LegacyMetricCardConfig[] = [
    {
      card_key: 'support_loaded_to_open',
      card_label: 'Support 1',
      card_title: 'Job Loaded To Open',
      metric_key: 'job.time_loaded_to_open',
      colour: '#7F8C8D',
    },
    {
      card_key: 'support_open_to_first',
      card_label: 'Support 2',
      card_title: 'Job Opened To 1st Attempt',
      metric_key: 'attempt.time_open_to_first_actual',
      colour: '#457EDD',
    },
    {
      card_key: 'support_loaded_to_first',
      card_label: 'Support 3',
      card_title: 'Job Loaded To 1st Attempt',
      metric_key: 'attempt.time_loaded_to_first_actual',
      colour: '#5D6D7E',
    },
    {
      card_key: 'support_open_to_second',
      card_label: 'Support 4',
      card_title: 'Job Opened To 2nd Attempt',
      metric_key: 'attempt.time_open_to_second_actual',
      colour: '#1F1F1F',
    },
    {
      card_key: 'support_open_to_third',
      card_label: 'Support 5',
      card_title: 'Job Opened To 3rd Attempt',
      metric_key: 'attempt.time_open_to_third_actual',
      colour: '#FA6F50',
    },
    {
      card_key: 'support_loaded_to_closed',
      card_label: 'Support 6',
      card_title: 'Job Loaded To Closed / Last Touch',
      metric_key: 'job.time_loaded_to_closed_or_last_touch',
      colour: '#62ADE5',
    },
  ];

  readonly quickRangeDays = [1, 7, 14, 30, 90, 180, 365, 730];
  readonly monthOptions = [
    { index: 0, label: 'Jan' },
    { index: 1, label: 'Feb' },
    { index: 2, label: 'Mar' },
    { index: 3, label: 'Apr' },
    { index: 4, label: 'May' },
    { index: 5, label: 'Jun' },
    { index: 6, label: 'Jul' },
    { index: 7, label: 'Aug' },
    { index: 8, label: 'Sep' },
    { index: 9, label: 'Oct' },
    { index: 10, label: 'Nov' },
    { index: 11, label: 'Dec' },
  ];

  search_model: ActivityMetricsV2Search = this.createDefaultSearch();
  response: ActivityMetricsV2Response | null = null;
  store_status: ActivityMetricsV2StoreStatus | null = null;
  report_error_message: string | null = null;
  loading = false;
  loading_started_at_ms: number | null = null;
  loading_elapsed_seconds = 0;
  loading_timer: ReturnType<typeof setInterval> | null = null;
  exporting = false;
  store_status_loading = false;
  showAdvancedFilters = false;
  activeQuickRangeDays: number | null = null;
  selectedCalendarYear = new Date().getFullYear();
  activeMonthIndex: number | null = null;

  picker_when_from: Date | null = null;
  picker_when_to: Date | null = null;

  brands_agent: any[] = [];
  brands_client: any[] = [];
  lookup_warning_messages: string[] = [];

  constructor(
    private demoService: DemoService,
    public readonly legalDate: LegalDateTimeService,
  ) {}

  ngOnInit(): void {
    this.applyDefaultMonthWindow();
    this.loadLookups();
    this.loadStoreStatus(null);
  }

  ngOnDestroy(): void {
    this.stopLoadingTimer();
  }

  createDefaultSearch(): ActivityMetricsV2Search {
    return {
      when_from: null,
      when_to: null,
      limit: ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
      projection: 'ui',
      backend_mode: 'auto',
      classic_lightweight: true,
      activity_sources: [...ACTIVITY_METRICS_V2_DEFAULT_SOURCES],
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: null,
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
      group_by: [...ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY],
      include_breakdowns: false,
      export_profile_key: 'timeline_audit',
      saved_file_name: 'agent-attempts-classic-2-v2',
    };
  }

  loadLookups(): void {
    this.lookup_warning_messages = [];

    this.loadArrayLookup('Agent brands / groups', this.demoService.getBrandsComboBox({ brand_type_id: 3 }), (items) => {
      this.brands_agent = items;
    });
    this.loadArrayLookup('Reporting clients', this.demoService.getBrandsComboBox({ brand_type_id: 2 }), (items) => {
      this.brands_client = items;
    });
  }

  loadMetrics(): void {
    const requestModel = this.buildRequestModel();
    this.loading = true;
    this.loading_started_at_ms = Date.now();
    this.loading_elapsed_seconds = 0;
    this.startLoadingTimer();
    this.report_error_message = null;
    this.loadStoreStatus(requestModel);

    this.demoService.getAgentActivityMetricsV2(requestModel)
      .pipe(finalize(() => {
        this.loading = false;
        this.loading_started_at_ms = null;
        this.stopLoadingTimer();
      }))
      .subscribe({
        next: (response) => {
          this.response = response;
          this.report_error_message = null;
        },
        error: (error) => {
          this.report_error_message = error?.error?.detail || error?.message || 'Failed to load Metrics 2.0 classic view.';
        },
      });
  }

  resetSearch(): void {
    this.search_model = this.createDefaultSearch();
    this.applyDefaultMonthWindow();
    this.loadStoreStatus();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyQuickRange(days: number, reload = true): void {
    const to = this.legalDate.toPickerDate(new Date()) ?? new Date();
    const from = new Date(to.getTime());
    from.setDate(from.getDate() - days);

    this.activeQuickRangeDays = days;
    this.picker_when_from = from;
    this.picker_when_to = to;
    this.syncCalendarSelection();

    if (reload) {
      this.loadMetrics();
    }
  }

  applyDefaultMonthWindow(): void {
    const defaultWindow = getActivityMetricsV2PreviousFullMonthWindow(this.legalDate.toPickerDate(new Date()) ?? new Date());
    this.selectedCalendarYear = defaultWindow.year;
    this.activeQuickRangeDays = null;
    this.activeMonthIndex = defaultWindow.monthIndex;
    this.picker_when_from = defaultWindow.from;
    this.picker_when_to = defaultWindow.to;
    this.syncCalendarSelection();
  }

  setMonthWindow(monthIndex: number): void {
    const whenFrom = new Date(this.selectedCalendarYear, monthIndex, 1, 0, 0, 0, 0);
    const whenTo = new Date(this.selectedCalendarYear, monthIndex + 1, 0, 23, 59, 0, 0);

    this.activeQuickRangeDays = null;
    this.activeMonthIndex = monthIndex;
    this.picker_when_from = whenFrom;
    this.picker_when_to = whenTo;
  }

  onCalendarYearChange(year: number | string): void {
    const numericYear = Number(year);
    if (!Number.isFinite(numericYear)) {
      return;
    }

    this.selectedCalendarYear = numericYear;

    if (this.activeMonthIndex !== null) {
      this.setMonthWindow(this.activeMonthIndex);
    }
  }

  onPickerChange(): void {
    this.activeQuickRangeDays = null;
    this.syncCalendarSelection();
  }

  exportMetrics(): void {
    const requestModel: ActivityMetricsV2Search = {
      ...this.buildRequestModel(),
      projection: 'audit',
    };

    this.exporting = true;
    this.demoService.exportAgentActivityMetricsV2(requestModel)
      .pipe(finalize(() => {
        this.exporting = false;
      }))
      .subscribe({
        next: (response) => this.downloadExportFile(response),
        error: (error) => {
          this.report_error_message = error?.error?.detail || error?.message || 'Failed to export Metrics 2.0 classic view.';
        },
      });
  }

  loadStoreStatus(requestModel?: ActivityMetricsV2Search | null): void {
    this.store_status_loading = true;

    const statusRequest = requestModel === null
      ? this.demoService.getAgentActivityMetricsV2StoreStatus()
      : this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel ?? this.buildRequestModel());

    statusRequest
      .pipe(finalize(() => {
        this.store_status_loading = false;
      }))
      .subscribe({
        next: (status) => {
          this.store_status = status;
        },
        error: () => {
          this.store_status = null;
        },
      });
  }

  buildRequestModel(): ActivityMetricsV2Search {
    return {
      ...this.search_model,
      when_from: this.picker_when_from ? this.legalDate.fromPickerDate(this.picker_when_from) : null,
      when_to: this.picker_when_to ? this.legalDate.fromPickerDate(this.picker_when_to) : null,
    };
  }

  get calendarYearOptions(): number[] {
    const currentYear = this.legalDate.toPickerDate(new Date())?.getFullYear() ?? new Date().getFullYear();
    const earliestYear = Math.max(2020, currentYear - 8);
    const years: number[] = [];

    for (let year = currentYear; year >= earliestYear; year -= 1) {
      years.push(year);
    }

    return years;
  }

  get metricCards(): Array<LegacyMetricCardConfig & { result: ActivityMetricsV2MetricResult | undefined }> {
    return this.metricCardConfig.map((config) => ({
      ...config,
      result: this.getMetricResult(config.metric_key),
    }));
  }

  get supportMetricCards(): Array<LegacyMetricCardConfig & { result: ActivityMetricsV2MetricResult | undefined }> {
    return this.supportMetricCardConfig.map((config) => ({
      ...config,
      result: this.getMetricResult(config.metric_key),
    }));
  }

  get totalAttempts(): number {
    return this.response?.summary?.total_attempts ?? 0;
  }

  get totalSubjects(): number {
    return this.response?.summary?.total_subjects ?? 0;
  }

  get totalAgents(): number {
    return this.response?.summary?.total_agents ?? 0;
  }

  get hasLoadedReport(): boolean {
    return !!this.response;
  }

  get resolvedBackendLabel(): string {
    return this.formatBackendMode(this.backendDecision?.resolved_backend_mode ?? this.search_model.backend_mode ?? 'auto');
  }

  get requestedBackendLabel(): string {
    return this.formatBackendMode(this.search_model.backend_mode ?? 'auto');
  }

  get backendDecision(): ActivityMetricsV2BackendDecision | null {
    return this.response?.diagnostics?.backend_decision ?? null;
  }

  get storeCoverage() {
    return this.store_status?.coverage ?? null;
  }

  get indexedThroughUtc(): string | null {
    const enabledSources = (this.store_status?.source_definitions ?? [])
      .filter((source) => source.is_enabled !== false && source.is_enabled !== 0)
      .map((source) => source.source_key);
    const sourceKeys = enabledSources.length
      ? new Set(enabledSources)
      : new Set((this.store_status?.sync_state ?? []).map((state) => state.source_key));

    const timestamps = (this.store_status?.sync_state ?? [])
      .filter((state) => sourceKeys.has(state.source_key))
      .map((state) => state.last_successful_canonical_timestamp_utc ?? null)
      .filter((value): value is string => !!value)
      .map((value) => Date.parse(value))
      .filter((value) => Number.isFinite(value));

    if (!timestamps.length) {
      return this.store_status?.latest_indexed_utc ?? null;
    }

    const syncStateIndexedThroughUtc = new Date(Math.min(...timestamps)).toISOString();
    const latestIndexedUtc = this.store_status?.latest_indexed_utc ?? null;
    if (!latestIndexedUtc) {
      return syncStateIndexedThroughUtc;
    }

    const syncStateIndexedThroughMs = Date.parse(syncStateIndexedThroughUtc);
    const latestIndexedMs = Date.parse(latestIndexedUtc);
    if (!Number.isFinite(syncStateIndexedThroughMs)) {
      return latestIndexedUtc;
    }
    if (!Number.isFinite(latestIndexedMs)) {
      return syncStateIndexedThroughUtc;
    }

    return new Date(Math.min(syncStateIndexedThroughMs, latestIndexedMs)).toISOString();
  }

  get indexedStatusSummary(): string {
    if (this.store_status_loading) {
      return 'Checking global Metrics 2.0 indexed status...';
    }

    if (!this.store_status) {
      return 'Global Metrics 2.0 indexed status has not been checked yet.';
    }

    if (!this.indexedThroughUtc) {
      return 'No global indexed-through timestamp is available yet across the enabled Metrics 2.0 sources.';
    }

    return `Global indexed-through point across enabled Metrics 2.0 sources: ${this.indexedThroughUtc}`;
  }

  get backendStatusAlertClass(): string {
    if (this.loading) {
      return 'alert alert-warning classic-runtime-alert';
    }

    if (!this.backendDecision) {
      return 'alert alert-info classic-runtime-alert';
    }

    return this.backendDecision.will_use_event_store
      ? 'alert alert-success classic-runtime-alert'
      : 'alert alert-warning classic-runtime-alert';
  }

  private syncCalendarSelection(): void {
    const whenFrom = this.picker_when_from;
    const whenTo = this.picker_when_to;

    if (!whenFrom || !whenTo) {
      this.activeMonthIndex = null;
      return;
    }

    this.selectedCalendarYear = whenFrom.getFullYear();

    const isSameMonth = whenFrom.getFullYear() === whenTo.getFullYear()
      && whenFrom.getMonth() === whenTo.getMonth();
    const isMonthStart = whenFrom.getDate() === 1
      && whenFrom.getHours() === 0
      && whenFrom.getMinutes() === 0;
    const expectedMonthEnd = new Date(whenFrom.getFullYear(), whenFrom.getMonth() + 1, 0, 23, 59, 0, 0);
    const isMonthEnd = whenTo.getFullYear() === expectedMonthEnd.getFullYear()
      && whenTo.getMonth() === expectedMonthEnd.getMonth()
      && whenTo.getDate() === expectedMonthEnd.getDate()
      && whenTo.getHours() === expectedMonthEnd.getHours()
      && whenTo.getMinutes() === expectedMonthEnd.getMinutes();

    this.activeMonthIndex = isSameMonth && isMonthStart && isMonthEnd
      ? whenFrom.getMonth()
      : null;
  }

  get backendRuntimeSummary(): string {
    const summaryParts = [
      `Default window ${this.estimateWindowDays()} day${this.estimateWindowDays() === 1 ? '' : 's'}`,
      `Requested ${this.requestedBackendLabel}`,
      `Resolved ${this.resolvedBackendLabel}`,
      `Timeline limit ${this.search_model.limit}`,
    ];

    if (this.backendDecision?.coverage_status) {
      summaryParts.splice(3, 0, `Coverage ${this.backendDecision.coverage_status}`);
    }

    return summaryParts.join(' | ');
  }

  get benchmarkSummaryCards(): Array<LegacyMetricCardConfig & {
    result: ActivityMetricsV2MetricResult | undefined;
    difference_days: number | null;
  }> {
    return this.metricCardConfig.map((config) => {
      const result = this.getMetricResult(config.metric_key);
      return {
        ...config,
        result,
        difference_days: this.msToDays(result?.difference_ms ?? null),
      };
    });
  }

  get loadingStatusMessage(): string {
    const sourceCount = this.search_model.activity_sources?.length ?? 0;
    const daySpan = this.estimateWindowDays();
    return `Fetching metric results across ${sourceCount} source${sourceCount === 1 ? '' : 's'} for roughly ${daySpan} day${daySpan === 1 ? '' : 's'}.`;
  }

  get loadingStatusDetail(): string {
    return `${this.backendRuntimeSummary} | Projection ${this.search_model.projection?.toUpperCase?.() || 'UI'}`;
  }

  get durationLabel(): string {
    if (!this.picker_when_from || !this.picker_when_to) {
      return '-';
    }

    const totalMs = this.picker_when_to.getTime() - this.picker_when_from.getTime();
    if (totalMs <= 0) {
      return '0m';
    }

    const totalMinutes = Math.floor(totalMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} D`);
    }

    if (hours > 0) {
      parts.push(`${hours} H`);
    }

    if (minutes > 0 || !parts.length) {
      parts.push(`${minutes} M`);
    }

    return parts.join(' ');
  }

  get subjectMetricChartRows(): LegacyMetricChartRow[] {
    return (this.response?.subjects ?? [])
      .slice()
      .sort((left, right) => (right.attempt_count ?? 0) - (left.attempt_count ?? 0))
      .slice(0, 20)
      .map((subject) => ({
        subject_number: subject.subject_number || `Subject ${subject.subject_id ?? '-'}`,
        metric_1: this.metricValueInDays(subject, 'attempt.time_instructions_to_first_actual'),
        metric_2: this.metricValueInDays(subject, 'attempt.time_instructions_to_second_actual'),
        metric_3: this.metricValueInDays(subject, 'attempt.time_first_to_second_actual'),
        metric_4: this.metricValueInDays(subject, 'attempt.time_instructions_to_third_actual'),
        metric_5: this.metricValueInDays(subject, 'attempt.time_second_to_third_actual'),
        metric_6: this.metricValueInDays(subject, 'job.time_open_to_closed_or_last_touch'),
      }));
  }

  get metricSummaryChartRows(): LegacyMetricSummaryChartRow[] {
    return this.metricCardConfig.map((config) => {
      const result = this.getMetricResult(config.metric_key);
      return {
        label: config.card_label,
        average_days: this.msToDays(result?.average_ms ?? null),
        benchmark_days: this.msToDays(result?.benchmark_ms ?? null),
      };
    });
  }

  get lifecycleBreakdownTables(): LegacyBreakdownTable[] {
    return [
      this.mapBreakdownTable('attempt_lifecycle', 'Attempt Lifecycle'),
      this.mapBreakdownTable('subject_lifecycle', 'Subject Lifecycle'),
      this.mapBreakdownTable('service_type', 'Service Type'),
      this.mapBreakdownTable('resolution_outcome', 'Resolution Outcome'),
      this.mapBreakdownTable('resolution_result', 'Resolution Result'),
      this.mapBreakdownTable('state', 'State'),
    ].filter((table): table is LegacyBreakdownTable => !!table);
  }

  get breakdownTablesDisabled(): boolean {
    return !this.search_model.include_breakdowns;
  }

  get subjectGridRows(): LegacySubjectGridRow[] {
    return (this.response?.subjects ?? [])
      .slice()
      .sort((left, right) => (right.attempt_count ?? 0) - (left.attempt_count ?? 0))
      .slice(0, 25)
      .map((subject) => ({
        job_id: subject.job_id ?? null,
        subject_id: subject.subject_id ?? null,
        latest_subject_service_id: subject.latest_subject_service_id,
        subject_number: subject.subject_number || '-',
        subject_name: subject.subject_name || '-',
        attempt_count: subject.attempt_count ?? 0,
        last_activity_at: subject.last_activity_at ?? null,
        latest_resolution_outcome_name: subject.latest_resolution_outcome_name,
        latest_service_type_name: subject.latest_service_type_name,
        region_source: subject.region_source,
        job_status: subject.job_status,
        has_stage_progression: !!subject.has_stage_progression,
        was_spawned_from_subject: !!subject.was_spawned_from_subject,
        metric_1: this.formatMetricValue(subject, 'attempt.time_instructions_to_first_actual'),
        metric_2: this.formatMetricValue(subject, 'attempt.time_instructions_to_second_actual'),
        metric_3: this.formatMetricValue(subject, 'attempt.time_first_to_second_actual'),
        metric_4: this.formatMetricValue(subject, 'attempt.time_instructions_to_third_actual'),
        metric_5: this.formatMetricValue(subject, 'attempt.time_second_to_third_actual'),
        metric_6: this.formatMetricValue(subject, 'job.time_open_to_closed_or_last_touch'),
      }));
  }

  get timelineRows(): LegacyTimelineRow[] {
    return (this.response?.timeline ?? [])
      .slice(0, 100)
      .map((event) => ({
        job_id: event.job_id,
        subject_id: event.subject_id,
        subject_service_id: event.subject_service_id,
        event_id: event.event_id,
        when: event.canonical_timestamp ?? null,
        subject_number: event.subject_number || '-',
        subject_name: event.subject_name || '-',
        source: event.source || '-',
        label: event.label || '-',
        action: event.action,
        agent_name: event.agent_name,
        service_type_name: event.service_type_name,
        resolution_outcome_name: event.resolution_outcome_name,
      }));
  }

  get sourceCountLabels(): string[] {
    return (this.response?.summary?.source_counts ?? []).map((sourceCount) => {
      return `${sourceCount.label} ${sourceCount.count}`;
    });
  }

  trackByMetricCard(index: number, item: LegacyMetricCardConfig): string {
    return item.card_key;
  }

  trackBySubjectNumber(index: number, item: LegacyMetricChartRow | LegacySubjectGridRow): string {
    return item.subject_number;
  }

  quickRangeLabel(days: number): string {
    return days === 1 ? 'Past 24 hrs' : days === 730 ? 'Past 2 YRS' : `Past ${days} Days`;
  }

  private loadArrayLookup<T>(label: string, request$: Observable<T>, assign: (items: any[]) => void): void {
    request$
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail || error?.message || 'Lookup request failed.';
          this.lookup_warning_messages = [...this.lookup_warning_messages, `${label}: ${detail}`];
          assign([]);
          return of([]);
        })
      )
      .subscribe((result) => {
        assign(Array.isArray(result) ? result : []);
      });
  }

  private startLoadingTimer(): void {
    this.stopLoadingTimer();
    this.loading_timer = setInterval(() => {
      if (!this.loading_started_at_ms) {
        this.loading_elapsed_seconds = 0;
        return;
      }

      this.loading_elapsed_seconds = Math.max(0, Math.floor((Date.now() - this.loading_started_at_ms) / 1000));
    }, 1000);
  }

  private stopLoadingTimer(): void {
    if (this.loading_timer) {
      clearInterval(this.loading_timer);
      this.loading_timer = null;
    }
  }

  private estimateWindowDays(): number {
    if (!this.picker_when_from || !this.picker_when_to) {
      return 0;
    }

    const fromTime = this.picker_when_from.getTime();
    const toTime = this.picker_when_to.getTime();
    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || toTime < fromTime) {
      return 0;
    }

    return Math.max(1, Math.ceil((toTime - fromTime) / 86400000));
  }

  private mapBreakdownTable(key: string, label: string): LegacyBreakdownTable | null {
    const breakdown = this.getBreakdown(key);
    if (!breakdown?.rows?.length) {
      return null;
    }

    return {
      key,
      label,
      rows: breakdown.rows.slice(0, 20).map((row) => ({
        label: row.label,
        count: row.count,
        event_count: row.event_count,
        attempt_count: row.attempt_count,
        rate: row.rate,
      })),
    };
  }

  private formatBackendMode(mode: ActivityMetricsV2Search['backend_mode'] | null | undefined): string {
    if (mode === 'event_store') {
      return 'Reporting Tables';
    }

    if (mode === 'live') {
      return 'Live Tables';
    }

    return 'Auto';
  }

  private getMetricResult(metricKey: string): ActivityMetricsV2MetricResult | undefined {
    return this.response?.metric_results?.find((metric) => metric.metric_key === metricKey);
  }

  private getBreakdown(breakdownKey: string): ActivityMetricsV2Breakdown | undefined {
    return this.response?.grouped_breakdowns?.find((breakdown) => breakdown.breakdown_key === breakdownKey);
  }

  private metricValueInDays(subject: ActivityMetricsV2SubjectRollup, metricKey: string): number | null {
    const rawValue = subject.metric_values?.[metricKey];
    return this.msToDays(rawValue);
  }

  private formatMetricValue(subject: ActivityMetricsV2SubjectRollup, metricKey: string): string {
    const rawValue = subject.metric_values?.[metricKey];

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }

    if (typeof rawValue === 'number') {
      return this.formatDuration(rawValue);
    }

    return `${rawValue}`;
  }

  private msToDays(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }

    return Number((numericValue / 86400000).toFixed(2));
  }

  private formatDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) {
      return '0m';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0) {
      parts.push(`${hours}h`);
    }

    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }

    if (!parts.length) {
      parts.push(`${totalSeconds}s`);
    }

    return parts.join(' ');
  }

  private downloadExportFile(response: ActivityMetricsV2ExportResponse): void {
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const columns = Array.isArray(response.columns) ? response.columns : [];

    if (!rows.length || !columns.length) {
      return;
    }

    const csvLines = [
      columns.join(','),
      ...rows.map((row) => columns.map((column) => this.escapeCsvValue(row[column])).join(',')),
    ];

    const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${response.profile?.file_name || this.search_model.saved_file_name || 'activity-metrics-v2-classic'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = `${value}`.replace(/"/g, '""');
    return `"${stringValue}"`;
  }
}
